import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, Flame } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { useLang } from "@/lib/i18n";
import { getConsent, saveConsent, ensureAnalytics, trackPageView } from "@/lib/analytics";

// DSGVO-konformer Consent-Banner im Krampus-Design.
// Erscheint nur beim ersten Besuch; Entscheidung wird in localStorage gespeichert.
export default function CookieConsent() {
  const { t, lang } = useLang();
  const [visible, setVisible] = useState(() => !getConsent());
  const [showSettings, setShowSettings] = useState(false);
  const [statistics, setStatistics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  const finish = (consent) => {
    saveConsent(consent);
    ensureAnalytics();
    setVisible(false);
  };

  const privacyPath = lang === "en" ? "/en/datenschutz" : "/datenschutz";

  // Bei erteilter Statistik-Einwilligung: GA laden und jeden Routenwechsel tracken.
  const { pathname } = useLocation();
  useEffect(() => {
    ensureAnalytics();
    trackPageView(pathname);
  }, [pathname]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          data-testid="cookie-banner"
          initial={{ y: 120, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 120, opacity: 0 }}
          transition={{ type: "spring", stiffness: 120, damping: 18 }}
          className="fixed bottom-0 inset-x-0 z-[100] px-4 pb-4 sm:px-6 sm:pb-6"
          role="dialog"
          aria-live="polite"
          aria-label={t("cookie_kicker")}
        >
          <div className="mx-auto max-w-3xl border border-hell-gold/30 bg-hell-coal/95 backdrop-blur-xl shadow-[0_20px_60px_rgba(0,0,0,0.7)]">
            <div className="h-px w-full bg-gradient-to-r from-transparent via-hell-gold to-transparent" />
            <div className="p-5 sm:p-7">
              <div className="flex items-start gap-4">
                <div className="hidden sm:flex h-11 w-11 shrink-0 items-center justify-center border border-hell-gold/40 text-hell-gold">
                  <Cookie className="h-5 w-5" strokeWidth={1.5} />
                </div>
                <div className="min-w-0">
                  <p className="font-cinzel text-[10px] tracking-[0.3em] uppercase text-hell-gold flex items-center gap-2">
                    <Flame className="h-3 w-3 animate-flicker" />
                    {t("cookie_kicker")}
                  </p>
                  <p className="mt-2 text-sm leading-relaxed text-hell-smoke">
                    {t("cookie_text")}{" "}
                    <Link
                      data-testid="cookie-privacy-link"
                      to={privacyPath}
                      className="text-hell-gold underline underline-offset-4 decoration-hell-gold/40 hover:decoration-hell-gold transition-colors"
                    >
                      {t("cookie_privacy")}
                    </Link>
                  </p>
                </div>
              </div>

              <AnimatePresence initial={false}>
                {showSettings && (
                  <motion.div
                    data-testid="cookie-settings-panel"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="mt-5 space-y-4 border-t border-white/10 pt-5">
                      <div className="flex items-center justify-between gap-4">
                        <div>
                          <p className="font-cinzel text-xs tracking-[0.15em] uppercase text-hell-bone">
                            {t("cookie_cat_necessary")}
                          </p>
                          <p className="mt-1 text-xs text-hell-smoke">{t("cookie_cat_necessary_d")}</p>
                        </div>
                        <span className="font-cinzel text-[10px] tracking-[0.2em] uppercase text-hell-gold whitespace-nowrap">
                          {t("cookie_always_on")}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-4">
                        <div>
                          <p className="font-cinzel text-xs tracking-[0.15em] uppercase text-hell-bone">
                            {t("cookie_cat_stats")}
                          </p>
                          <p className="mt-1 text-xs text-hell-smoke">{t("cookie_cat_stats_d")}</p>
                        </div>
                        <Switch
                          data-testid="cookie-statistics-toggle"
                          checked={statistics}
                          onCheckedChange={setStatistics}
                          aria-label={t("cookie_cat_stats")}
                        />
                      </div>
                      <div className="flex items-center justify-between gap-4">
                        <div>
                          <p className="font-cinzel text-xs tracking-[0.15em] uppercase text-hell-bone">
                            {t("cookie_cat_marketing")}
                          </p>
                          <p className="mt-1 text-xs text-hell-smoke">{t("cookie_cat_marketing_d")}</p>
                        </div>
                        <Switch
                          data-testid="cookie-marketing-toggle"
                          checked={marketing}
                          onCheckedChange={setMarketing}
                          aria-label={t("cookie_cat_marketing")}
                        />
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <button
                  data-testid="cookie-accept-all-button"
                  onClick={() => finish({ statistics: true, marketing: true })}
                  className="font-cinzel text-xs tracking-[0.2em] uppercase bg-hell-gold text-hell-black px-6 py-3 hover:bg-hell-ember hover:text-hell-bone transition-colors"
                >
                  {t("cookie_accept_all")}
                </button>
                <button
                  data-testid="cookie-reject-button"
                  onClick={() => finish({ statistics: false, marketing: false })}
                  className="font-cinzel text-xs tracking-[0.2em] uppercase border border-hell-gold/40 text-hell-gold px-6 py-3 hover:bg-hell-gold/10 transition-colors"
                >
                  {t("cookie_necessary")}
                </button>
                {showSettings ? (
                  <button
                    data-testid="cookie-save-button"
                    onClick={() => finish({ statistics, marketing })}
                    className="font-cinzel text-xs tracking-[0.2em] uppercase border border-white/20 text-hell-bone px-6 py-3 hover:border-hell-gold/60 hover:text-hell-gold transition-colors"
                  >
                    {t("cookie_save")}
                  </button>
                ) : (
                  <button
                    data-testid="cookie-settings-button"
                    onClick={() => setShowSettings(true)}
                    className="font-cinzel text-xs tracking-[0.2em] uppercase border border-white/20 text-hell-bone px-6 py-3 hover:border-hell-gold/60 hover:text-hell-gold transition-colors"
                  >
                    {t("cookie_settings")}
                  </button>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
